import Enumerable from "linq";
import Moment from "moment";
import { IConfigurationRegistry } from "../../../../base/domain/configuration";
import { Directory, instanceOfIRectangleCoordinatable, IContentSource } from "../../../../base/domain/drawing";
import { AuthenticatedService } from "../../../../base/infrastructure";
import { ObjectUtility } from "../../../../base/utilities";
import { NewObjectKeys, IConfiguration } from "../../../domain/configuration";
import {
  IEditableFormatRepository,
  EditableFormatSummary,
  IEditableFormatStructure,
  IEditableFormatObject,
  EditHistorySearchParameter,
  IEditableFormatOperation,
  RevertFormatEditionResult,
  FormatStructureEditor,
  FormatRegistrationResult,
} from "../../../domain/editableFormat";
import {
  EditableFormatImageSource,
  EditableFormatLayerImageSource,
  EditableFormatTemporaryImageSource,
} from "../../../domain/editableFormat/sources";
import { IImageDrawableObject } from "../../../domain/editableFormat/objects";
import {
  IEditableFormatBean,
  IEditableFormatFolderRootBean,
  IEditableFormatOperationBean,
  IFormatObjectBean,
  IFormatRegisterResultBean,
  IXElementContentsObjectBean,
} from "./beans";
import { TranslatorFactory } from "./translators";

export class EditableFormatRepository extends AuthenticatedService implements IEditableFormatRepository {
  constructor(getToken: () => string, private readonly _configurationRegistry: IConfigurationRegistry<IConfiguration>) {
    super(getToken);
    this._translatorFactory = new TranslatorFactory(_configurationRegistry);
  }

  private readonly _translatorFactory: TranslatorFactory;

  async getFormatTree(): Promise<Directory<number, EditableFormatSummary>[]> {
    const url = "api/format-editor/tree/hierarchy";
    const result = await this.get<IEditableFormatFolderRootBean>(url);
    return this._translatorFactory.create().toFormatsTree(() => this.token, result.data);
  }

  async getFormat(id: number): Promise<EditableFormatSummary> {
    const url = "api/format-editor/format/" + id;
    const result = await this.get<IEditableFormatBean>(url);
    return this._translatorFactory.create().toFormat(() => this.token, result.data);
  }

  async getFormatArchives(id: number): Promise<EditableFormatSummary[]> {
    const url = "api/format-editor/format/" + id + "/archives";
    const result = await this.get<IEditableFormatBean[]>(url);
    return this._translatorFactory.create().toFormats(() => this.token, result.data);
  }

  async getNewFormatStructure(version: string): Promise<IEditableFormatStructure> {
    const url = "api/format-editor/format/new";
    const result = await this.get<IFormatObjectBean>(
      url,
      {
        params: {
          version: version,
        }
      });
    return this._translatorFactory.create().toFormatStructure(result.data);
  }

  async getFormatStructure(id: number): Promise<IEditableFormatStructure> {
    const url = "api/format-editor/format/" + id + "/edit";
    const result = await this.get<IFormatObjectBean>(url);
    return this._translatorFactory.create().toFormatStructure(result.data);
  }

  async getNewCopiedFormatStructure(id: number, archiveId: number): Promise<IEditableFormatStructure> {
    const url = "api/format-editor/format/" + id + "/copy";
    const result = await this.get<IFormatObjectBean>(
      url,
      {
        params: {
          archiveId: archiveId,
        }
      });
      
    const format = this._translatorFactory.create().toFormatStructure(result.data);
    // 貼り付け画像をキャッシュテーブルから取得するよう情報を書き換える
    Enumerable.from(format.pages)
      .forEach(async p => Enumerable.from(p.objects)
        .forEach(async o => {
          if (o.elementName === "image" || o.elementName === "imagearea") {
            const imageObj = (o as IImageDrawableObject);
            imageObj.temporaryImageContent.isEnable = !!imageObj.imageContent.guid;
            imageObj.temporaryImageContent.guid = imageObj.imageContent.guid;
          }
        }));

    return format;
  }

  async getNewCopiedLatestBackgroundImageGuid(id: number, imageGuid: string): Promise<string> {
    const url = "api/format-editor/format/" + id + "/background/" + imageGuid + "/copy";
    const result = await this.get<string>(url);
    return result.data;
  }

  createNewFormatObject(key: NewObjectKeys, formatId: number): IEditableFormatObject {
    const result = this._translatorFactory.create().toFormatPageObject(this.getNewFormatObjectBean(key), formatId);
    if (instanceOfIRectangleCoordinatable(result)) {
      // 位置は指定されていても意味がないので(0,0)に.
      result.location.x = 0;
      result.location.y = 0;
      // サイズは0だと配置中のオフセット計算で0除算が発生するので20x20で補完する.(0以上ならそのまま)
      result.size.width = (0 < result.size.width) ? result.size.width : 20;
      result.size.height = (0 < result.size.height) ? result.size.height : 20;
    }
    return result;
  }

  private getNewFormatObjectBean(key: NewObjectKeys): IXElementContentsObjectBean {
    const configuration = this._configurationRegistry.getConfigurationService().getConfiguration();
    return ObjectUtility.copyDeeply(configuration.formatEditor.behavior.format.editor.newObjects[key].source);
  }

  async getUserEditedFormats(id: string): Promise<EditableFormatSummary[]> {
    const url = "api/format-editor/format/edited/" + id;
    const result = await this.get<IEditableFormatBean[]>(url);
    return this._translatorFactory.create().toFormats(() => this.token, result.data);
  }

  async getNoneDisplayFormats(): Promise<EditableFormatSummary[]> {
    const url = "api/format-editor/format/none-display";
    const result = await this.get<IEditableFormatBean[]>(url);
    return this._translatorFactory.create().toFormats(() => this.token, result.data);
  }

  async getDeletedFormats(): Promise<EditableFormatSummary[]> {
    const url = "api/format-editor/format/deleted";
    const result = await this.get<IEditableFormatBean[]>(url);
    return this._translatorFactory.create().toFormats(() => this.token, result.data);
  }

  async getUnsynchronizedFormats(): Promise<EditableFormatSummary[]> {
    const url = "api/format-sync/list";
    const result = await this.get<IEditableFormatBean[]>(url);
    return this._translatorFactory.create().toFormats(() => this.token, result.data);
  }

  async getEditHistory(param: EditHistorySearchParameter): Promise<IEditableFormatOperation[]> {
    const url = "api/format-editor/format/operations";
    const query = {
      from: this.toDateString(param.dateDuration.from),
      to: this.toDateString(param.dateDuration.to),
    };
    const result = await this.post<IEditableFormatOperationBean[]>(url, query);
    return this._translatorFactory.create().toFormatOperations(result.data);
  }

  private toDateString(source: Date): string {
    return source ? Moment(source).format("YYYYMMDD") : null;
  }

  async registerFormatStructure(format: FormatStructureEditor, isTemporary: boolean): Promise<FormatRegistrationResult> {
    const url = "api/format-editor/format/save";
    const formatBean = this._translatorFactory.create().toFormatObjectBean(format.getSource(), isTemporary);
    console.debug(formatBean);
    const result = await this.post(url, formatBean);
    console.debug(result.data);
    return this.toFormatRegistrationResult(result.data);
  }

  private toFormatRegistrationResult(bean: IFormatRegisterResultBean): FormatRegistrationResult {
    return new FormatRegistrationResult(
      bean.isOk,
      bean.isOk
        ? {
          id: bean.formatId,
          archiveId: bean.archiveId,
        }
        : {
          isDeleted: bean.items.isDeleted,
          edition: bean.items.edition,
          updated: {
            userName: bean.items.updateUserName,
            teminal: bean.items.updateTerminal,
            dateTime: bean.items.updateDateTime,
          },
        }
    );
  }

  async revertFormatEdition(id: number, archiveId: number): Promise<RevertFormatEditionResult> {
    const url = "api/format-editor/format/" + id + "/revert";
    const result = await this.get<RevertFormatEditionResult>(
      url,
      {
        params: {
          revertArchiveId: archiveId,
        }
      });
    return result.data;
  }

  async reviveFormat(id: number, parent: number): Promise<void> {
    const url = "api/format-editor/format/" + id + "/revive";
    await this.get<FormatRegistrationResult>(
      url,
      {
        params: {
          parentId: parent,
        }
      });
  }

  async synchronizeFormats(formats: EditableFormatSummary[]): Promise<void> {
    const url = "api/format-sync/synchronize";
    const query = Enumerable.from(formats).select(s => {
      return {
        formatId: s.id,
        archiveId: s.archiveId,
      };
    }).toArray();
    console.debug(query);
    await this.post<void>(url, query);
    return;
  }


  async addDirectory(name: string, parent: number, index: number): Promise<number> {
    const url = "api/format-editor/tree/new-folder";
    const query = {
      parentId: parent,
      name: name,
      index: index,
    };
    const result = await this.post<number>(url, query);
    return result.data;
  }

  async moveDirectory(id: number, parent: number, index: number): Promise<void> {
    const url = "api/format-editor/tree/move-folder";
    const query = {
      parentId: parent,
      folderId: id,
      index: index,
    };
    await this.post<void>(url, query);
  }

  async deleteDirectory(id: number): Promise<void> {
    const url = "api/format-editor/tree/delete-folder";
    const query = {
      folderId: id,
    };
    await this.post<void>(url, query);
  }

  async renameDirectory(id: number, name: string): Promise<void> {
    const url = "api/format-editor/tree/rename-folder";
    const query = {
      folderId: id,
      name: name,
    };
    await this.post<void>(url, query);
  }

  async addFormat(id: number, name: string, parent: number, index: number): Promise<void> {
    const url = "api/format-editor/tree/new-format";
    const query = {
      parentId: parent,
      formatId: id,
      name: !name ? "" : name,
      index: index,
    };
    await this.post<void>(url, query);
  }

  async moveFormat(id: number, parent: number, index: number): Promise<void> {
    const url = "api/format-editor/tree/move-format";
    const query = {
      parentId: parent,
      formatId: id,
      index: index,
    };
    await this.post<void>(url, query);
  }

  async deleteFormat(id: number): Promise<void> {
    const url = "api/format-editor/tree/delete-format";
    const query = {
      formatId: id,
    };
    await this.post<void>(url, query);
  }

  async renameFormat(id: number, name: string): Promise<void> {
    const url = "api/format-editor/tree/rename-format";
    const query = {
      formatId: id,
      name: name,
    };
    await this.post<void>(url, query);
  }

  async redisplayFormat(id: number, parent: number): Promise<void> {
    const url = "api/format-editor/tree/redisplay-format";
    const query = {
      formatId: id,
      parentId: parent,
    };
    await this.post<void>(url, query);
  }

  async uploadImageFile(contents: string): Promise<string> {
    const url = "api/format-editor/temporary/image-save";
    const query = {
      image: contents,
    };
    const result = await this.post<string>(url, query);
    return result.data;
  }

  async uploadReferenceFile(fileName: string, contents: string): Promise<string> {
    const url = "api/format-editor/temporary/reference-save";
    const query = {
      fileName: fileName,
      contents: contents,
    };
    const result = await this.post<string>(url, query);
    return result.data;
  }

  async getNewTemporaryImageGuid(imageGuid: string): Promise<string> {
    const url = "api/format-editor/temporary/image/" + imageGuid + "/copy";
    const result = await this.get<string>(url);
    return result.data;
  }

  getImageSource(formatId: number, imageGuid: string): IContentSource {
    return new EditableFormatImageSource(() => this.token, formatId, null, 1, imageGuid);
  }

  getLayerImageSource(formatId: number, imageGuid: string): IContentSource {
    return new EditableFormatLayerImageSource(() => this.token, formatId, imageGuid);
  }

  getTemporaryImageSource(imageGuid: string): IContentSource {
    return new EditableFormatTemporaryImageSource(() => this.token, 2, imageGuid);
  }
}
